import React, { FunctionComponent, ReactNode, useCallback, useMemo, useState } from 'react'

import { Theme } from '@lyrahealth-inc/shared-app-logic'

import { ForcedThemeContext, ForcedThemeState } from './ForcedThemeContext'

export interface ForcedThemeProviderProps {
  children: ReactNode
}

/**
 * Provider component that enables programmatic theme overrides.
 * This should wrap the ThemeProvider or be placed at the app root level.
 *
 * When forcedTheme is set (via useForcedDarkMode hook), it takes precedence over
 * the user's theme preference. This is used for flows like Triage and Universal Search
 * that require dark mode regardless of user settings.
 */
export const ForcedThemeProvider: FunctionComponent<ForcedThemeProviderProps> = ({ children }) => {
  const [forcedDarkModeCount, setForcedDarkModeCount] = useState(0)

  const forcedTheme = forcedDarkModeCount > 0 ? Theme.DARK : null

  const registerForcedDarkMode = useCallback(() => {
    setForcedDarkModeCount((prev) => prev + 1)
  }, [])

  const unregisterForcedDarkMode = useCallback(() => {
    setForcedDarkModeCount((prev) => Math.max(0, prev - 1))
  }, [])

  const value = useMemo<ForcedThemeState>(
    () => ({
      forcedTheme,
      registerForcedDarkMode,
      unregisterForcedDarkMode,
    }),
    [forcedTheme, registerForcedDarkMode, unregisterForcedDarkMode],
  )

  return <ForcedThemeContext.Provider value={value}>{children}</ForcedThemeContext.Provider>
}
